/**
 * The gate for every route that renders the authenticated shell (epic 076
 * task 03 as amended by QA round 18, #134 + #137). The layout mounts only
 * after the provider's mount-time `me` check resolves:
 *
 * - in flight: a neutral full-screen loading frame. The shell never renders
 *   optimistically, so no screen's data fetch can fire under a session that
 *   may not exist — the zero-filled dashboard + red error-banner flash of
 *   #134 is structurally impossible;
 * - resolved signed-out: a redirect to /login carrying the current path (and
 *   query) as `return`. This is the navigation the failed bootstrap check
 *   drives: App mounts the gate on the `/` route whose `*` child catches
 *   every path the router serves — so a non-route path like /health (which
 *   nginx's SPA fallback still serves as index.html) gets the same treatment
 *   as a protected route (#137) instead of a permanent white screen;
 * - resolved signed-in: the shell itself.
 *
 * The gate is deliberately NOT wrapped around /login and /change-password:
 * they render outside the Layout and must paint without waiting for the
 * check, and a signed-out `me` 401 received ON /login must stay put (the
 * r1 H-3 redirect-loop guard).
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Layout } from '../layout/layout';

export function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center text-sm text-muted"
      >
        Loading…
      </div>
    );
  }

  if (user === null) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?return=${returnTo}`} replace />;
  }

  return <Layout />;
}
